// =============================================================================
// SURVEYJS FORM BUILDER - SurveyJS Creator wrapper with custom styling
// =============================================================================

import { useEffect, useRef } from 'react';
import { SurveyCreator, SurveyCreatorComponent } from 'survey-creator-react';
import { Serializer } from 'survey-core';
import 'survey-core/survey-core.min.css';
import 'survey-creator-core/survey-creator-core.min.css';

import { useFormBuilderStore } from './store';
import { convertFormToSurveyJS, convertSurveyJSToForm } from './adapters/surveyjs-adapter';
import type { Form } from './types';

// =============================================================================
// CUSTOM FIELD TYPES CONFIGURATION
// =============================================================================

// Register custom property for primitive fields
Serializer.addProperty('question', {
  name: 'primitiveType',
  category: 'general',
  visibleIndex: 3,
  choices: ['person', 'organization', 'document', 'asset', 'transaction', 'workflow', 'event', 'location', 'none'],
  default: 'none',
});

// =============================================================================
// SURVEYJS CREATOR OPTIONS
// =============================================================================

const creatorOptions = {
  showLogicTab: true,
  showTranslationTab: false,
  showJSONEditorTab: true,
  showPreviewTab: true,
  showThemeTab: false,
  showSidebar: true,
  haveCommercialLicense: false,
  isAutoSave: false,
  showToolbox: true,
  questionTypes: [
    'text',
    'comment',
    'dropdown',
    'checkbox',
    'radiogroup',
    'rating',
    'boolean',
    'file',
    'signaturepad',
    'panel',
    'html',
  ],
};

// =============================================================================
// CREATOR STYLING
// =============================================================================

const injectCustomStyles = () => {
  const styleId = 'surveyjs-custom-styles';
  if (document.getElementById(styleId)) return;

  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    /* SurveyJS Creator custom styling for Tailwind/Shadcn consistency */

    .svc-creator {
      --sjs-primary-backcolor: #3B82F6;
      --sjs-primary-backcolor-light: #DBEAFE;
      --sjs-primary-backcolor-dark: #2563EB;
      --sjs-primary-forecolor: #ffffff;
      --sjs-secondary-backcolor: #F9FAFB;
      --sjs-border-light: #E5E7EB;
      --sjs-border-default: #D1D5DB;
      --sjs-general-backcolor: #ffffff;
      --sjs-general-forecolor: #111827;
      --sjs-general-dim-forecolor: #6B7280;
      --sjs-font-family: ui-sans-serif, system-ui, sans-serif;
      --sjs-corner-radius: 0.5rem;
      --sjs-base-unit: 8px;
    }

    .dark .svc-creator {
      --sjs-primary-backcolor: #60A5FA;
      --sjs-primary-backcolor-light: #1E3A5F;
      --sjs-primary-backcolor-dark: #93C5FD;
      --sjs-secondary-backcolor: #1F2937;
      --sjs-border-light: #374151;
      --sjs-border-default: #4B5563;
      --sjs-general-backcolor: #111827;
      --sjs-general-forecolor: #F9FAFB;
      --sjs-general-dim-forecolor: #9CA3AF;
    }

    /* Toolbox styling */
    .svc-toolbox {
      background: var(--sjs-general-backcolor);
      border-right: 1px solid var(--sjs-border-light);
    }

    .svc-toolbox__item {
      border-radius: var(--sjs-corner-radius);
      transition: all 0.15s ease;
    }

    .svc-toolbox__item:hover {
      background: var(--sjs-secondary-backcolor);
    }

    /* Property grid styling */
    .spg-root-modern {
      background: var(--sjs-general-backcolor);
      border-left: 1px solid var(--sjs-border-light);
    }

    .spg-panel__title {
      font-weight: 600;
      color: var(--sjs-general-forecolor);
    }

    /* Question styling */
    .svc-question__content {
      border-radius: var(--sjs-corner-radius);
      border: 1px solid var(--sjs-border-light);
      background: var(--sjs-general-backcolor);
    }

    .svc-question__content:hover {
      border-color: var(--sjs-primary-backcolor);
    }

    .svc-question__content--selected {
      border-color: var(--sjs-primary-backcolor);
      box-shadow: 0 0 0 3px var(--sjs-primary-backcolor-light);
    }

    /* Tab bar styling */
    .svc-tabbed-menu {
      background: var(--sjs-general-backcolor);
      border-bottom: 1px solid var(--sjs-border-light);
    }

    .svc-tabbed-menu-item {
      color: var(--sjs-general-dim-forecolor);
      transition: all 0.15s ease;
    }

    .svc-tabbed-menu-item--selected {
      color: var(--sjs-primary-backcolor);
      border-bottom-color: var(--sjs-primary-backcolor);
    }

    /* Input styling */
    .spg-input,
    .spg-dropdown {
      border-radius: var(--sjs-corner-radius);
      border: 1px solid var(--sjs-border-default);
      background: var(--sjs-general-backcolor);
    }

    .spg-input:focus,
    .spg-dropdown:focus {
      border-color: var(--sjs-primary-backcolor);
      outline: none;
      box-shadow: 0 0 0 2px var(--sjs-primary-backcolor-light);
    }

    /* Button styling */
    .sv-action-bar-item {
      border-radius: var(--sjs-corner-radius);
      transition: all 0.15s ease;
    }

    .sv-action-bar-item:hover {
      background: var(--sjs-secondary-backcolor);
    }

    /* Canvas area */
    .svc-tab-designer {
      background: var(--sjs-secondary-backcolor);
    }

    .svc-page__content {
      background: var(--sjs-general-backcolor);
      border-radius: var(--sjs-corner-radius);
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }
  `;
  document.head.appendChild(style);
};

// =============================================================================
// COMPONENT
// =============================================================================

interface SurveyJSFormBuilderProps {
  initialForm?: Form;
  onSave?: (form: Form) => void;
  onAutoSave?: (form: Form) => void;
}

export function SurveyJSFormBuilder({
  initialForm,
  onSave,
  onAutoSave,
}: SurveyJSFormBuilderProps) {
  const creatorRef = useRef<SurveyCreator | null>(null);
  const setForm = useFormBuilderStore((s) => s.setForm);
  const exportForm = useFormBuilderStore((s) => s.exportForm);

  // Initialize creator
  useEffect(() => {
    injectCustomStyles();

    const creator = new SurveyCreator(creatorOptions);

    // Load initial form if provided
    if (initialForm) {
      const surveyJSON = convertFormToSurveyJS(initialForm);
      creator.JSON = surveyJSON;
    }

    // Save callback
    creator.saveSurveyFunc = (saveNo: number, callback: (num: number, success: boolean) => void) => {
      const surveyJSON = creator.JSON;
      const form = convertSurveyJSToForm(surveyJSON, initialForm);

      setForm(form);

      if (onSave) {
        onSave(form);
      }

      callback(saveNo, true);
    };

    // Auto-save on changes (debounced)
    let autoSaveTimeout: NodeJS.Timeout;
    creator.onModified.add(() => {
      clearTimeout(autoSaveTimeout);
      autoSaveTimeout = setTimeout(() => {
        const surveyJSON = creator.JSON;
        const form = convertSurveyJSToForm(surveyJSON, initialForm);

        if (onAutoSave) {
          onAutoSave(form);
        }
      }, 1000);
    });

    creatorRef.current = creator;

    return () => {
      clearTimeout(autoSaveTimeout);
    };
  }, [initialForm, onSave, onAutoSave, setForm]);

  // These methods are available for external use via ref if needed
  void function handleSave() {
    if (creatorRef.current) {
      creatorRef.current.doSave();
    }
  };

  void function getCurrentForm(): Form | null {
    if (creatorRef.current) {
      const surveyJSON = creatorRef.current.JSON;
      return convertSurveyJSToForm(surveyJSON, initialForm);
    }
    return exportForm();
  };

  if (!creatorRef.current) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-secondary-500">Loading form builder...</div>
      </div>
    );
  }

  return (
    <div className="h-full w-full">
      <SurveyCreatorComponent creator={creatorRef.current} />
    </div>
  );
}

// =============================================================================
// EXPORT UTILITIES
// =============================================================================

export { convertFormToSurveyJS, convertSurveyJSToForm };
